import { useCallback } from 'react';

/**
 * Hook personalizado para exportar reportes
 * Proporciona funcionalidad de impresión/exportación a PDF
 */
const useReportExport = () => {
  /**
   * Exportar reporte como PDF usando el diálogo de impresión del navegador
   * @param {string} reportTitle - Título del reporte para el documento
   * @param {string} contentSelector - Selector CSS del contenido a exportar (default: '.reportes-detail-page')
   */
  const exportToPDF = useCallback((reportTitle = 'Reporte', contentSelector = '.reportes-detail-page') => {
    try {
      // Guardar el título original
      const originalTitle = document.title;

      // Cambiar el título del documento para el PDF
      document.title = reportTitle;

      // Agregar clase para estilos de impresión
      const contentElement = document.querySelector(contentSelector);
      if (contentElement) {
        contentElement.classList.add('printing');
      }

      // Ocultar elementos que no deben aparecer en el PDF
      const elementsToHide = document.querySelectorAll('.no-print, .btn-export, .btn-back, .reportes-filters');
      elementsToHide.forEach(el => {
        el.style.display = 'none';
      });

      // Esperar un momento para que se apliquen los estilos
      setTimeout(() => {
        // Abrir diálogo de impresión
        window.print();

        // Restaurar elementos después de imprimir
        setTimeout(() => {
          elementsToHide.forEach(el => {
            el.style.display = '';
          });

          if (contentElement) {
            contentElement.classList.remove('printing');
          }

          // Restaurar título original
          document.title = originalTitle;
        }, 500);
      }, 300);

      return true;
    } catch (error) {
      console.error('Error al exportar PDF:', error);
      return false;
    }
  }, []);

  /**
   * Exportar datos como CSV
   * @param {Array} data - Array de objetos con los datos
   * @param {string} filename - Nombre del archivo
   * @param {Array} columns - Array de objetos {key, label} con las columnas a exportar
   */
  const exportToCSV = useCallback((data, filename = 'reporte.csv', columns = []) => {
    try {
      if (!data || data.length === 0) {
        alert('No hay datos para exportar');
        return false;
      }

      // Si no se especifican columnas, usar todas las keys del primer objeto
      const cols = columns.length > 0
        ? columns
        : Object.keys(data[0]).map(key => ({ key, label: key }));

      // Crear encabezados CSV
      const headers = cols.map(col => col.label).join(',');

      // Crear filas CSV
      const rows = data.map(row => {
        return cols.map(col => {
          const value = row[col.key];

          // Manejar valores especiales
          if (value === null || value === undefined) return '';

          // Escapar comillas y comas
          const stringValue = String(value);
          if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
            return `"${stringValue.replace(/"/g, '""')}"`;
          }

          return stringValue;
        }).join(',');
      });

      // Combinar todo
      const csv = [headers, ...rows].join('\n');

      // Crear blob y descargar
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');

      if (navigator.msSaveBlob) {
        // IE 10+
        navigator.msSaveBlob(blob, filename);
      } else {
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      return true;
    } catch (error) {
      console.error('Error al exportar CSV:', error);
      alert('Error al exportar a CSV');
      return false;
    }
  }, []);

  /**
   * Exportar tabla HTML como Excel (formato HTML que Excel puede abrir)
   * @param {string} tableSelector - Selector CSS de la tabla
   * @param {string} filename - Nombre del archivo
   */
  const exportToExcel = useCallback((tableSelector = '.reportes-table', filename = 'reporte.xls') => {
    try {
      const table = document.querySelector(tableSelector);

      if (!table) {
        alert('No se encontró la tabla para exportar');
        return false;
      }

      // Clonar tabla para no modificar la original
      const tableClone = table.cloneNode(true);

      // Remover columnas de acciones si existen
      const actionColumns = tableClone.querySelectorAll('.actions-column, .no-export');
      actionColumns.forEach(col => col.remove());

      // Crear HTML para Excel
      const html = `
        <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
          <head>
            <meta charset="utf-8">
            <!--[if gte mso 9]>
            <xml>
              <x:ExcelWorkbook>
                <x:ExcelWorksheets>
                  <x:ExcelWorksheet>
                    <x:Name>Reporte</x:Name>
                    <x:WorksheetOptions>
                      <x:DisplayGridlines/>
                    </x:WorksheetOptions>
                  </x:ExcelWorksheet>
                </x:ExcelWorksheets>
              </x:ExcelWorkbook>
            </xml>
            <![endif]-->
          </head>
          <body>
            ${tableClone.outerHTML}
          </body>
        </html>
      `;

      // Crear blob y descargar
      const blob = new Blob([html], { type: 'application/vnd.ms-excel' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      return true;
    } catch (error) {
      console.error('Error al exportar a Excel:', error);
      alert('Error al exportar a Excel');
      return false;
    }
  }, []);

  return {
    exportToPDF,
    exportToCSV,
    exportToExcel
  };
};

export default useReportExport;
